import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function SectionTitle({ children }) {
  return <h2 style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.4rem' }}>{children}</h2>;
}

function KV({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.45rem 0', borderBottom: '1px solid var(--divider)' }}>
      <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: highlight || 'var(--text-1)' }}>{value}</span>
    </div>
  );
}

function MonthPicker({ value, onChange }) {
  return (
    <input
      type="month"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ padding: '0.4rem 0.75rem', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text-1)', fontSize: '0.85rem' }}
    />
  );
}

function DownloadBtn({ data, filename, label }) {
  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }} onClick={handleDownload}>
      ↓ {label}
    </button>
  );
}

// ── Management Report ──────────────────────────────────────────────────────

function ManagementReport() {
  const [month, setMonth] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['report-management', month],
    queryFn: () => adminService.getManagementReport(month || undefined).then(r => r.data.data),
  });

  const nplColor = data?.portfolio_health?.npl_ratio < 5 ? '#00C896' : data?.portfolio_health?.npl_ratio < 10 ? '#F5A623' : '#FF4757';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <MonthPicker value={month} onChange={setMonth} />
        <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Leave blank for previous month</span>
        {data && <DownloadBtn data={data} filename={`management-report-${data.period}.json`} label="Download JSON" />}
      </div>

      {isLoading && <LoadingSpinner />}
      {isError && <div className="alert alert-error"><span>⚠</span><span>Failed to generate report.</span></div>}

      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(0,200,150,0.06)', borderRadius: 10, border: '1px solid rgba(0,200,150,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Monthly Management Report — {data.period}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>Generated {new Date(data.generated_at).toLocaleString()}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Users */}
            <div className="card">
              <SectionTitle>Customer Acquisition</SectionTitle>
              <KV label="New Registrations" value={data.users.new_registrations.toLocaleString()} />
              <KV label="KYC Approved This Month" value={data.users.kyc_approved.toLocaleString()} highlight="#00C896" />
              <KV label="KYC Pending (Pipeline)" value={data.users.kyc_pending_pipeline.toLocaleString()} highlight="#F5A623" />
            </div>

            {/* Lending */}
            <div className="card">
              <SectionTitle>Lending Activity</SectionTitle>
              <KV label="New Applications" value={data.lending.new_applications.toLocaleString()} />
              <KV label="Approved" value={data.lending.approved.toLocaleString()} highlight="#00C896" />
              <KV label="Rejected" value={data.lending.rejected.toLocaleString()} highlight="#FF4757" />
              <KV label="Approval Rate" value={`${data.lending.approval_rate}%`} highlight="#00C896" />
              <KV label="Loans Disbursed" value={`${data.lending.disbursed_count} loans`} />
              <KV label="Amount Disbursed" value={formatCurrency(data.lending.disbursed_value)} highlight="var(--primary)" />
            </div>

            {/* Collections */}
            <div className="card">
              <SectionTitle>Collections</SectionTitle>
              <KV label="Total Collected This Month" value={formatCurrency(data.collections.total_collected)} highlight="#00C896" />
            </div>

            {/* Portfolio health */}
            <div className="card">
              <SectionTitle>Portfolio Health</SectionTitle>
              <KV label="Active Portfolio (Outstanding)" value={formatCurrency(data.portfolio_health.active_portfolio)} />
              <KV label="NPL Loans" value={data.portfolio_health.npl_count.toLocaleString()} highlight={nplColor} />
              <KV label="NPL Value" value={formatCurrency(data.portfolio_health.npl_value)} highlight={nplColor} />
              <KV label="NPL Ratio" value={`${data.portfolio_health.npl_ratio}%`} highlight={nplColor} />
            </div>

            {/* Loan type mix */}
            {data.loan_type_mix?.length > 0 && (
              <div className="card">
                <SectionTitle>Loan Type Mix</SectionTitle>
                {data.loan_type_mix.map(({ type, count }) => (
                  <KV key={type} label={type.charAt(0).toUpperCase() + type.slice(1)} value={`${count} applications`} />
                ))}
              </div>
            )}

            {/* Compliance */}
            <div className="card">
              <SectionTitle>Compliance & Fraud</SectionTitle>
              <KV label="Fraud Alerts Raised" value={data.compliance.fraud_alerts_raised.toLocaleString()} highlight={data.compliance.fraud_alerts_raised > 0 ? '#F5A623' : undefined} />
              <KV label="Fraud Alerts Resolved" value={data.compliance.fraud_alerts_resolved.toLocaleString()} highlight="#00C896" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Credit Bureau Report ───────────────────────────────────────────────────

function CreditBureauReport() {
  const [month, setMonth] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['report-credit-bureau', month],
    queryFn: () => adminService.getCreditBureauReport(month || undefined).then(r => r.data.data),
  });

  const [expandedId, setExpandedId] = useState(null);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <MonthPicker value={month} onChange={setMonth} />
        <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Leave blank for previous month</span>
        {data && (
          <DownloadBtn
            data={data}
            filename={`credit-bureau-report-${data.period}.json`}
            label="Download for Bureau Submission"
          />
        )}
      </div>

      <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'rgba(91,142,255,0.06)', borderRadius: 10, border: '1px solid rgba(91,142,255,0.2)', fontSize: '0.82rem', color: 'var(--text-2)' }}>
        <strong style={{ color: 'var(--text-1)' }}>Note:</strong> This report follows the standard credit bureau data submission format (CRC/FirstCentral). Download the JSON file and upload to your bureau integration portal, or pass it to your data mapping layer for CSV/XML conversion.
      </div>

      {isLoading && <LoadingSpinner />}
      {isError && <div className="alert alert-error"><span>⚠</span><span>Failed to generate report.</span></div>}

      {data && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontWeight: 700 }}>Credit Bureau Report — {data.period}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{data.total_accounts} accounts · Generated {new Date(data.generated_at).toLocaleString()}</div>
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Account No.</th>
                    <th>Borrower</th>
                    <th>Type</th>
                    <th>Approved</th>
                    <th>Outstanding</th>
                    <th>Status</th>
                    <th>On-Time</th>
                    <th>Late</th>
                    <th>DPD</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((acc, i) => (
                    <>
                      <tr key={acc.facility.account_number || i} style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === i ? null : i)}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{acc.account_number}</td>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{acc.borrower.full_name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{acc.borrower.email}</div>
                        </td>
                        <td style={{ textTransform: 'capitalize', fontSize: '0.82rem' }}>{acc.loan_type}</td>
                        <td style={{ fontSize: '0.82rem' }}>{formatCurrency(acc.facility.approved_amount)}</td>
                        <td style={{ fontSize: '0.82rem', color: acc.facility.outstanding_balance > 0 ? '#F5A623' : 'var(--text-2)' }}>
                          {formatCurrency(acc.facility.outstanding_balance)}
                        </td>
                        <td>
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: 99, textTransform: 'uppercase',
                            background: acc.facility.status === 'active' ? 'rgba(0,200,150,0.12)' : acc.facility.status === 'defaulted' ? 'rgba(255,71,87,0.12)' : 'rgba(148,163,184,0.12)',
                            color: acc.facility.status === 'active' ? '#00C896' : acc.facility.status === 'defaulted' ? '#FF4757' : '#94a3b8',
                          }}>
                            {acc.facility.status}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#00C896' }}>{acc.performance.paid_on_time}</td>
                        <td style={{ fontSize: '0.82rem', color: acc.performance.late_payments > 0 ? '#F5A623' : 'var(--text-2)' }}>{acc.performance.late_payments}</td>
                        <td style={{ fontSize: '0.82rem', color: acc.performance.days_past_due > 0 ? '#FF4757' : 'var(--text-2)', fontWeight: acc.performance.days_past_due > 0 ? 700 : 400 }}>
                          {acc.performance.days_past_due > 0 ? `${acc.performance.days_past_due}d` : '—'}
                        </td>
                        <td style={{ color: 'var(--text-3)', fontSize: '0.75rem' }}>{expandedId === i ? '▲' : '▼'}</td>
                      </tr>
                      {expandedId === i && (
                        <tr key={`${i}-detail`}>
                          <td colSpan={10} style={{ background: 'var(--surface-alt)', padding: '1rem 1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
                              <div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Borrower</div>
                                <div style={{ fontSize: '0.82rem' }}>{acc.borrower.full_name}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{acc.borrower.phone}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{acc.borrower.address}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{acc.borrower.state}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginTop: 4 }}>BVN: {acc.borrower.bvn || '—'}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>NIN: {acc.borrower.nin || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Facility</div>
                                <div style={{ fontSize: '0.82rem' }}>Amount: {formatCurrency(acc.facility.approved_amount)}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Rate: {acc.facility.interest_rate}% p.a.</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Tenure: {acc.facility.tenure_months} months</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Start: {acc.facility.start_date || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Performance</div>
                                <div style={{ fontSize: '0.82rem' }}>Total Installments: {acc.performance.total_installments}</div>
                                <div style={{ fontSize: '0.78rem', color: '#00C896' }}>On-time: {acc.performance.paid_on_time}</div>
                                <div style={{ fontSize: '0.78rem', color: acc.performance.late_payments > 0 ? '#F5A623' : 'var(--text-2)' }}>Late: {acc.performance.late_payments}</div>
                                <div style={{ fontSize: '0.78rem', color: acc.performance.overdue_installments > 0 ? '#FF4757' : 'var(--text-2)' }}>Overdue: {acc.performance.overdue_installments}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Last Payment</div>
                                <div style={{ fontSize: '0.82rem' }}>{acc.performance.last_payment_date || '—'}</div>
                                {acc.performance.last_payment_amount > 0 && (
                                  <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{formatCurrency(acc.performance.last_payment_amount)}</div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                  {!data.accounts.length && (
                    <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No accounts for this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────

const TABS = ['Management Report', 'Credit Bureau Report'];

export default function Reports() {
  const [tab, setTab] = useState(0);

  return (
    <div>
      <div className="page-header">
        <h1>Reports</h1>
        <p>Monthly management summaries and credit bureau submission packages</p>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: 'var(--surface)', borderRadius: 12, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === i ? 700 : 400,
            background: tab === i ? 'linear-gradient(135deg, rgba(0,200,150,0.2), rgba(0,200,150,0.08))' : 'transparent',
            color: tab === i ? 'var(--primary)' : 'var(--text-2)',
            transition: 'all .15s',
          }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && <ManagementReport />}
      {tab === 1 && <CreditBureauReport />}
    </div>
  );
}
